import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useContext } from 'react';

import { FormattedMessage, T } from '@flipster/i18n';
import { useOverlayItem } from '@flipster/overlay';
import { SubAccountIdContext } from '@flipster/r230522/normal-sub/sub-account';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import TrashIcon from '@flipster/react/icon/generated/TrashIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { MovableModal } from '../../shared/movable-modal';
import { useDeleteTpSl } from '../../shared/trade-actions';
import { runTpslConfirm } from './liquidation-warning';
import { SymbolInfoSection } from './sections/1-symbol-info-section';
import { SetTriggerConditionSection } from './sections/2-set-trigger-condition-section';
import { SetQuantitySection } from './sections/3-set-quantity-section';
import { OrderInfoSection } from './sections/4-order-info-section';
import { SetLimitPriceSection } from './sections/5-set-limit-price-section';
import { tpslFormContentBunja } from './state';

interface Props {
  onConfirm?: () => void;
}

export const TpslFormContent = ({ onConfirm }: Props) => {
  const overlay = useOverlayItem();
  const { isMobile } = useBreakpoint();

  const {
    symbol,
    selectedOrder,
    tpslType,
    isTimeTriggerOrder,
    onConfirmButtonClick,
    canPartial,
    canLimitTpslAtom,
    submitDisabledAtom,
    tpslTabAtom,
    secondaryButtonTypeAtom,
    isLimitTpslOrderAtom,
    isCloseToLiquidationAtom,
  } = useBunja(tpslFormContentBunja);
  const accountId = useContext(SubAccountIdContext);

  const isCloseToLiquidation = useAtomValue(isCloseToLiquidationAtom);
  const submitDisabled = useAtomValue(submitDisabledAtom);

  const confirmTpsl = async () => {
    await onConfirmButtonClick();
    onConfirm?.();
  };

  // 청산 경고 다이얼로그. Proceed를 누르면 넘겨받은 onProceed(=confirmTpsl)를 실행한다.
  const openLiquidationWarningDialog = (onProceed: () => Promise<void> | void) => {
    dialog.open({
      title: {
        label: <T id='Autogen_common.r251126.s_Liquidation_notice' />,
        leftAddon: <CautionCircleIcon size={24} color={token('colors.content.state.warning')} />,
      },
      content: (
        <div>
          <FormattedMessage id='Autogen_common.r251126.s_Your_trigger_price_is_close_to_liquidation_price' />{' '}
          <FormattedMessage
            id='Trade.r260625.Liquidation_notice_message_if_reaches_mark_link'
            values={{
              markPrice: (
                <IsomorphicLink
                  href='https://flipsterxyz.zendesk.com/hc/en-us/articles/7353316085647-Mark-Price-Calculation'
                  className={css({ color: 'content.default.level-5', textDecoration: 'underline' })}
                  external
                  newTab
                >
                  <FormattedMessage id='Autogen_common.r251126.s_Mark_Price_Link' />
                </IsomorphicLink>
              ),
            }}
          />
        </div>
      ),
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            label={<T id='Kyc.r250311.Proceed' />}
            onClick={async () => {
              await onProceed();
              dialog.close();
            }}
          />
        ),
        secondary: (
          <Dialog.Button
            hierarchy='secondary'
            color='neutral'
            label={<T id='Common.r250401.Cancel' />}
            onClick={() => dialog.close()}
          />
        ),
      },
    });
  };

  // 트리거 가격이 청산가에 근접하면 곧바로 실행하지 않고 청산 경고를 먼저 띄운다.
  // (TimeTriggerTpslModal / native와 동일한 UX)
  const handleConfirmClick = () => runTpslConfirm({ isCloseToLiquidation, confirmTpsl, openLiquidationWarningDialog });
  const tpslTab = useAtomValue(tpslTabAtom);
  const secondaryButtonType = useAtomValue(secondaryButtonTypeAtom);
  const showQuantitySection = tpslTab === 'partial' && canPartial;
  const canLimitTpsl = useAtomValue(canLimitTpslAtom);
  const isLimitTpslOrderChecked = useAtomValue(isLimitTpslOrderAtom);
  const isLimitTpslOrder = canLimitTpsl && isLimitTpslOrderChecked;

  const { deleteTpslQuery } = useDeleteTpSl();
  const { isPending, mutateAsync: deleteTpsl } = deleteTpslQuery;

  const triggerConditionTitle = isTimeTriggerOrder ? (
    <T id='Trade.r250401.Trigger_when_price_moves' />
  ) : (
    <T id='Trade.r250401.Trigger_price' />
  );

  return (
    <>
      <MovableModal.Content>
        <div className={stack({ gap: 24 })}>
          <SymbolInfoSection />
          <SetTriggerConditionSection title={triggerConditionTitle} />
          {isLimitTpslOrder && <SetLimitPriceSection />}
          {showQuantitySection && <SetQuantitySection />}
          <OrderInfoSection />
        </div>
      </MovableModal.Content>
      <MovableModal.Footer align={isMobile ? 'vertical' : 'horizontal'}>
        {secondaryButtonType === 'remove' && (
          <MovableModal.Button
            hierarchy='secondary'
            size='large'
            color='neutral'
            label={<T id='Common.r250401.Remove' />}
            icon={<TrashIcon size={20} />}
            isLoading={isPending}
            onClick={() => {
              if (!selectedOrder) throw new Error('Selected order not found');

              dialog.open({
                title: {
                  label:
                    tpslType === 'tp' ? (
                      <T id='Position.r250401.Remove_tp_title' />
                    ) : (
                      <T id='Position.r250401.Remove_sl_title' />
                    ),
                },
                content:
                  tpslType === 'tp' ? (
                    <T id='Position.r250401.Remove_tp_description' />
                  ) : (
                    <T id='Position.r250401.Remove_sl_description' />
                  ),
                buttons: {
                  primary: (
                    <Dialog.Button
                      hierarchy='primary'
                      label={<T id='Common.r250401.Confirm' />}
                      onClick={async () => {
                        await deleteTpsl({ symbol, orderId: selectedOrder.orderId, tpslType, accountId });
                        overlay.close();
                        dialog.close();
                      }}
                    />
                  ),
                  secondary: (
                    <Dialog.Button
                      hierarchy='secondary'
                      color='neutral'
                      label={<T id='Common.r250401.Cancel' />}
                      onClick={() => {
                        dialog.close();
                      }}
                    />
                  ),
                },
              });
            }}
          />
        )}
        <MovableModal.Button
          hierarchy='primary'
          size='large'
          color='accent'
          label={<T id='Common.r250401.Confirm' />}
          disabled={submitDisabled}
          onClick={handleConfirmClick}
        />
      </MovableModal.Footer>
    </>
  );
};
